/**
 * Frontend mirror of `backend/services/scoringService.js`.
 *
 * Identical algorithm — used by SensitivityAnalysis (E4) to re-rank
 * client-side as the user slides criterion weights, with no server
 * round-trip per slider tick. Also consumed by ScoringWorkspace (E2),
 * ScoringVisualization, and ComparisonScoring (F3) wherever the rollup
 * shape is needed without a full DB query.
 *
 * Linear: MYD-206 (E1).
 *
 * **PARITY INVARIANT**: any change to the algorithm here must be applied
 * verbatim to `backend/services/scoringService.js`, and vice-versa. The
 * Node test in `backend/services/scoringService.test.js` runs identical
 * canonical inputs through both sides; if it diverges, both copies need
 * to come back into sync.
 */

const SCORE_DECIMAL_PLACES = 2

function round(n: number): number {
	if (!Number.isFinite(n)) return 0
	const factor = Math.pow(10, SCORE_DECIMAL_PLACES)
	return Math.round(n * factor) / factor
}

function getId(obj: unknown): string {
	if (!obj || typeof obj !== 'object') return ''
	const o = obj as Record<string, unknown>
	if (o._id != null) return String(o._id)
	if (o.id != null) return String(o.id)
	return ''
}

export interface CriterionInput {
	_id?: unknown
	id?: unknown
	name: string
	weight: number
	category?: string | null
	sortOrder?: number
}

export interface SiteInput {
	_id?: unknown
	id?: unknown
	name: string
}

export interface ScoreInput {
	site: unknown
	criterion: unknown
	value: number
	justification?: string | null
}

export interface CriterionScore {
	criterionId: string
	name: string
	weight: number
	avgScore: number | null
	scoreCount: number
	justification: string | null
}

export interface SiteScoreResult {
	siteId: string
	siteName: string
	overallScore: number | null
	totalWeight: number
	scoredWeight: number
	criterionScores: CriterionScore[]
}

export interface RankingEntry {
	siteId: string
	siteName: string
	rank: number
	overallScore: number | null
}

export interface CriterionResult {
	id: string
	name: string
	weight: number
	category: string | null
	sortOrder: number
}

export interface WeightedScoreResult {
	criteria: CriterionResult[]
	sites: SiteScoreResult[]
	ranking: RankingEntry[]
	totalWeight: number
}

export interface ComputeWeightedScoresInput {
	criteria?: CriterionInput[]
	sites?: SiteInput[]
	scores?: ScoreInput[]
}

/**
 * Compute weighted scoring for one evaluation. Pure function — no I/O.
 * Algorithm matches the backend service line-for-line; the parity test in
 * `backend/services/scoringService.test.js` locks the contract.
 */
export function computeWeightedScores({
	criteria = [],
	sites = [],
	scores = [],
}: ComputeWeightedScoresInput = {}): WeightedScoreResult {
	const criteriaList = Array.isArray(criteria) ? criteria : []
	const sitesList = Array.isArray(sites) ? sites : []
	const scoresList = Array.isArray(scores) ? scores : []

	const totalWeight = criteriaList.reduce((sum, c) => sum + (Number(c.weight) || 0), 0)

	const siteResults: SiteScoreResult[] = sitesList.map((site) => {
		const siteId = getId(site)
		const siteScores = scoresList.filter((s) => getId({ _id: s.site }) === siteId)

		let weightedTotal = 0
		let scoredWeight = 0

		const criterionScores: CriterionScore[] = criteriaList.map((criterion) => {
			const criterionId = getId(criterion)
			const matching = siteScores.filter(
				(s) => getId({ _id: s.criterion }) === criterionId,
			)
			const scoreCount = matching.length
			const avgScore =
				scoreCount > 0
					? round(
							matching.reduce((sum, s) => sum + (Number(s.value) || 0), 0) / scoreCount,
						)
					: null

			if (avgScore != null) {
				const weight = Number(criterion.weight) || 0
				weightedTotal += avgScore * weight
				scoredWeight += weight
			}

			const justification =
				matching.find((s) => s.justification && String(s.justification).trim())
					?.justification ?? null

			return {
				criterionId,
				name: criterion.name,
				weight: Number(criterion.weight) || 0,
				avgScore,
				scoreCount,
				justification,
			}
		})

		const overallScore =
			scoredWeight > 0 ? round(weightedTotal / scoredWeight) : null

		return {
			siteId,
			siteName: site.name,
			overallScore,
			totalWeight,
			scoredWeight,
			criterionScores,
		}
	})

	const ranking: RankingEntry[] = [...siteResults]
		.sort((a, b) => {
			const aScore = a.overallScore ?? -Infinity
			const bScore = b.overallScore ?? -Infinity
			if (aScore !== bScore) return bScore - aScore
			return String(a.siteName ?? '').localeCompare(String(b.siteName ?? ''))
		})
		.map((site, index) => ({
			siteId: site.siteId,
			siteName: site.siteName,
			rank: index + 1,
			overallScore: site.overallScore,
		}))

	const criteriaResult: CriterionResult[] = criteriaList.map((c) => ({
		id: getId(c),
		name: c.name,
		weight: Number(c.weight) || 0,
		category: c.category ?? null,
		sortOrder: typeof c.sortOrder === 'number' ? c.sortOrder : 0,
	}))

	return {
		criteria: criteriaResult,
		sites: siteResults,
		ranking,
		totalWeight,
	}
}
